/**
 * Request Deduplication System
 *
 * Prevents duplicate requests from being sent to the server by:
 * 1. Creating a hash of the request (procedure + input)
 * 2. Storing pending requests in memory
 * 3. Returning the same promise for duplicate requests
 * 4. Clearing the cache after the request completes
 *
 * This is an industry-standard solution used by Zapier, n8n, and other platforms
 * to prevent infrastructure overload from duplicate/concurrent requests.
 *
 * The TTL (2000ms) ensures that even if a request is lost, it will be retried
 * after a reasonable timeout.
 */

interface PendingRequest {
  promise: Promise<any>;
  timestamp: number;
}

const pendingRequests = new Map<string, PendingRequest>();
const DEDUP_TTL = 2000; // 2 seconds - requests older than this are pruned

/**
 * Generate a unique key for a request based on path and input
 */
export function generateRequestKey(path: string, input: unknown): string {
  try {
    // Create a stable string representation of the input
    const inputStr = JSON.stringify(input);
    return `${path}:${inputStr}`;
  } catch {
    // If we can't serialize the input, just use the path
    // This means the deduplication won't work for this request, but it won't break
    return path;
  }
}

/**
 * Check if a request is already pending and return the existing promise
 */
export function getOrCreatePendingRequest(
  key: string,
  executor: () => Promise<any>
): Promise<any> {
  const now = Date.now();

  // Clean up old pending requests
  for (const [k, v] of pendingRequests.entries()) {
    if (now - v.timestamp > DEDUP_TTL) {
      pendingRequests.delete(k);
    }
  }

  // Check if we have a pending request for this key
  const existing = pendingRequests.get(key);
  if (existing) {
    return existing.promise;
  }

  // Create a new promise and store it
  const promise = executor().finally(() => {
    // Clean up after the request completes
    pendingRequests.delete(key);
  });

  pendingRequests.set(key, {
    promise,
    timestamp: now,
  });

  return promise;
}

/**
 * Get the current number of pending requests (useful for monitoring)
 */
export function getPendingRequestCount(): number {
  return pendingRequests.size;
}

/**
 * Clear all pending requests (useful for testing or cleanup)
 */
export function clearPendingRequests(): void {
  pendingRequests.clear();
}
